/**
 * Pure logic behind the SymbolUniverse strip (TASK-20260823-pairs-universe-ui).
 *
 * A self-universe strategy (Shishito) ships its ~400-name panel in the same comma-joined `symbols`
 * field every ≤10-pair strategy uses. The strip collapses any list to ONE row of the space it is
 * given (as many chips as fit, then a "+N more" pill) and expands inline. Everything that can be
 * decided over plain data is decided here, under Vitest; the component only measures chip widths
 * once and renders (docs/engineering/TDD.md — "SPA: extract pure logic into lib/").
 *
 * Entries carry BOTH forms of a pair: `raw` is the token exactly as the host sent it (what a series
 * fetch or a select event must echo back) and `display` is the slash form an operator reads.
 *
 * @typedef {{ raw: string, display: string }} SymbolEntry
 * @typedef {{ left: number, top: number, width: number }} ChipOffset
 */
import { fromRequestSymbol } from './symbolFormat.js';

/**
 * How many chips the strip considers for the collapsed row before it has measured anything. A seed,
 * not a ceiling: when the whole probe fits, the strip doubles it and fits again, so an ultrawide
 * row never under-fills. 64 is past what any ordinary desktop row holds, so the second pass is rare.
 */
export const PROBE_SEED = 64;

/** Canonical comparison key: slash form, upper case, trimmed. '' for nothing. */
export function normalizePair(pair) {
  if (typeof pair !== 'string') return '';
  return fromRequestSymbol(pair.trim()).toUpperCase();
}

/**
 * Parses the host's comma-joined `symbols` field into deduplicated display entries.
 * Order-preserving; on a duplicate the FIRST raw token wins. Anything that is not a string → [].
 *
 * @param {string | null | undefined} csv
 * @returns {SymbolEntry[]}
 */
export function parseSymbols(csv) {
  if (typeof csv !== 'string' || csv.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const token of csv.split(',')) {
    const raw = token.trim();
    if (!raw) continue;
    const display = normalizePair(raw);
    if (seen.has(display)) continue;
    seen.add(display);
    out.push({ raw, display });
  }
  return out;
}

/**
 * Simulates a `flex-wrap: wrap` row of fixed-width chips: each chip starts after the previous one
 * plus `gap`, and wraps to the next row when it would cross `containerWidth`. `top` is the ROW
 * INDEX (0, 1, 2 …), which is all fitToRow needs. Pure, so a resize re-fits with no DOM work.
 *
 * @param {readonly number[]} widths
 * @param {{ gap: number, containerWidth: number }} box
 * @returns {ChipOffset[]}
 */
export function layoutRow(widths, { gap, containerWidth }) {
  const out = [];
  let left = 0;
  let row = 0;
  for (const width of widths) {
    if (left > 0 && left + width > containerWidth) { left = 0; row++; }
    out.push({ left, top: row, width });
    left += width + gap;
  }
  return out;
}

/**
 * Decides how many leading chips stay visible on the first row so that the "+N more" pill ALWAYS
 * fits on that same row. Chips share the first row when their `top` matches the first chip's
 * (within half a pixel — offsets may be fractional). When anything is hidden, chips are evicted from
 * the right until the pill fits after the last visible one.
 *
 * @param {{ offsets: readonly ChipOffset[], total: number, containerWidth: number, pillWidth: number, gap: number }} args
 * @returns {{ visible: number, hidden: number }}
 */
export function fitToRow({ offsets, total, containerWidth, pillWidth, gap }) {
  if (!(total > 0) || !offsets || offsets.length === 0) return { visible: 0, hidden: total > 0 ? total : 0 };

  const firstTop = offsets[0].top;
  let visible = 0;
  while (visible < offsets.length && Math.abs(offsets[visible].top - firstTop) < 0.5) visible++;

  if (visible >= total) return { visible: total, hidden: 0 }; // everything sits on the row — no pill

  while (visible > 0) {
    const last = offsets[visible - 1];
    if (last.left + last.width + gap + pillWidth <= containerWidth) break;
    visible--;
  }
  return { visible, hidden: total - visible };
}

/**
 * Case- and delimiter-insensitive substring filter over the display form. An empty query returns
 * the SAME array (so callers can cheaply detect "unfiltered").
 *
 * @param {SymbolEntry[]} entries
 * @param {string} query
 * @returns {SymbolEntry[]}
 */
export function filterSymbols(entries, query) {
  const q = normalizePair(query ?? '');
  if (!q) return entries;
  return entries.filter((e) => e.display.includes(q));
}

/**
 * Text-only rendering for tooltips, aria labels and confirm dialogs: "A, B, C … +N more" — no suffix
 * when everything fits within `max`. Accepts the host's raw CSV or already-parsed entries, so a
 * text-only site needs one import. CSV results are memoized: list rows re-evaluate their tooltip on
 * every store replacement, and the string is the same 4 KB every time.
 *
 * @param {string | readonly SymbolEntry[] | null | undefined} input
 * @param {number} [max=3]
 */
export function summarizeSymbols(input, max = 3) {
  if (typeof input === 'string') {
    const key = `${max}|${input}`;
    let hit = summaryCache.get(key);
    if (hit === undefined) {
      hit = summarizeEntries(parseSymbols(input), max);
      if (summaryCache.size > 256) summaryCache.clear();
      summaryCache.set(key, hit);
    }
    return hit;
  }
  return summarizeEntries(input ?? [], max);
}
const summaryCache = new Map();

function summarizeEntries(entries, max) {
  if (entries.length === 0) return '';
  const head = entries.slice(0, max).map((e) => e.display).join(', ');
  return entries.length <= max ? head : `${head} … +${entries.length - max} more`;
}

/**
 * Moves the selected pair to the front so it survives the collapse (the active symbol of a
 * selectable strip must never vanish behind the pill). Returns the input untouched when the
 * selection is absent, missing from the list, or already first.
 *
 * @param {SymbolEntry[]} entries
 * @param {string | null | undefined} selected  request- or display-form
 */
export function pinFirst(entries, selected) {
  const key = normalizePair(selected);
  if (!key) return entries;
  const idx = entries.findIndex((e) => e.display === key);
  if (idx <= 0) return entries;
  return [entries[idx], ...entries.slice(0, idx), ...entries.slice(idx + 1)];
}
